'use client'

import { Component, ErrorInfo, ReactNode } from 'react'

import { WaitingWithData } from './WaitingWithData'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
}

export class DragDropErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  }

  public static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Note: do NOT auto-reset `hasError` on a timer here. Doing so turns any error
    // that is thrown during render (for example, a `RangeError: Invalid Date` from
    // a date helper) into an infinite render loop, because the boundary keeps
    // re-mounting children that throw the same error again. If recovery is needed,
    // it should be triggered by an explicit user action or a key change instead.
    console.error('DragDrop error:', error, errorInfo)
  }

  public render() {
    if (this.state.hasError) {
      // This boundary wraps the whole CMS: a null fallback silently swallows
      // render crashes and leaves whatever was last painted frozen on screen
      // (that masked a real outage). Always show a recovery UI instead.
      return (
        this.props.fallback || (
          <WaitingWithData
            message="Something went wrong."
            secondaryMessage="An unexpected error occurred. Please reload the page. If the problem persists, contact support."
            buttonText="Reload page"
            buttonCallback={() => window.location.reload()}
            loadingIndicatorIsDisabled={true}
          />
        )
      )
    }

    return this.props.children
  }
}
